<template>
  <div class="route-view dark has-top">
    <div class="logo">
      <img src="/images/logo.png" />
    </div>

    <div class="page-header">
      <h1 class="page-title">{{ $t('ranking.pageTitle') }}</h1>
    </div>

    <div
      v-if="!loading && !milestones.length"
      class="status-box m-t-20 text-center"
    >
      {{ $t('ranking.noCampaignMsg') }}
    </div>

    <template v-else-if="!loading && milestones.length">
      <!-- Rank tier timeline -->
      <ranking-timeline
        :ranks="ranks"
        :current-rank-index="currentRankIndex"
        :active-rank-index="activeRankIndex"
        @node-click="openRankDetail"
      />

      <!-- Active rank display -->
      <div class="current-rank-section flex-between">
        <div class="rank-name-display">
          {{ activeRank.name }}
        </div>
        <div class="rank-badge-area">
          <img
            v-if="activeRank.image"
            :src="activeRank.image"
            :alt="activeRank.name"
          />
          <div v-else class="rank-badge-placeholder">
            {{ activeRank.shortName[0] }}
          </div>
        </div>
      </div>

      <!-- Progress card -->
      <div class="rank-card">
        <div class="progress-header flex-between">
          <span>{{ $t('ranking.depositProgress') }}</span>
          <span>
            {{ userDeposit }} /
            <strong class="goal-amount">{{ activeRank.goal }}</strong>
          </span>
        </div>
        <progress
          class="progress rank-progress"
          :value="userDeposit"
          :max="activeRank.goal"
        ></progress>
        <div class="reward-claimed-label">
          {{ $t('ranking.rewardClaimedLabel') }}
          <div class="reward-claimed-count">
            <span class="claimed-x">{{ claimedCount }}</span
            >/{{ ranks.length }}
          </div>
        </div>

        <!-- <div class="text-center m-t-15 m-b-10">
          <button
            class="button claim-btn"
            :disabled="!canClaim"
            @click="claimBonus"
          >
            {{ $t('ranking.claimBtn', { bonus: activeRank.bonus }) }}
          </button>
        </div>
        <div class="status-box">
          {{ statusMessage }}
        </div> -->
      </div>

      <!-- Benefits card -->
      <!-- <div class="rank-card benefits-card m-t-15">
        <div class="benefits-title">{{ $t('ranking.benefitsTitle') }}</div>
        <div class="benefit-item">
          {{ $t('ranking.freeCreditLabel') }}
          <span class="amount-teal">{{ activeRank.freeCredit }}</span>
          {{ $t('ranking.baht') }}
        </div>
        <div class="benefit-item">
          {{ $t('ranking.successAmountLabel') }}
          <span class="amount-yellow">{{ activeRank.successAmount }}</span>
          {{ $t('ranking.baht') }}
        </div>
        <div class="benefit-item">
          <template v-if="activeRank.isLimitWithdraw">
            {{ $t('ranking.maxWithdrawLabel') }}
            <span class="amount-red">{{ activeRank.limitWithdrawAmount }}</span>
            {{
              activeRank.limitWithdrawType === 'BAHT'
                ? $t('ranking.baht')
                : $t('ranking.times')
            }}
          </template>
          <span v-else>{{ $t('ranking.noWithdrawLimit') }}</span>
        </div>
        <hr class="rank-divider" />
        <div class="rank-reset-note">
          {{ $t('ranking.resetNote') }}
        </div>
      </div> -->

      <!-- Rank detail modal -->
      <div class="modal light" :class="{ 'is-active': modalActive }">
        <div class="modal-background" @click="closeModal"></div>
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">{{ $t('ranking.modalTitle') }}</p>
            <button
              class="delete"
              aria-label="close"
              @click="closeModal"
            ></button>
          </header>
          <section class="modal-card-body" v-if="selectedRank">
            <div class="flex-between m-b-20">
              <div class="rank-name-display modal-rank-name">
                {{ selectedRank.name }}
              </div>
              <div class="rank-badge-area modal-badge">
                <img
                  v-if="selectedRank.image"
                  :src="selectedRank.image"
                  :alt="selectedRank.name"
                />
                <div v-else class="rank-badge-placeholder">
                  {{ selectedRank.shortName[0] }}
                </div>
              </div>
            </div>

            <div class="rank-card">
              <div class="progress-header flex-between">
                <span>{{ $t('ranking.depositProgress') }}</span>
                <span>
                  {{ userDeposit }} /
                  <strong class="goal-amount">{{ selectedRank.goal }}</strong>
                </span>
              </div>
              <progress
                class="progress rank-progress"
                :value="userDeposit"
                :max="selectedRank.goal"
              ></progress>
            </div>

            <div class="rank-card benefits-card m-t-15">
              <div class="benefits-title">
                {{ $t('ranking.benefitsTitle') }}
              </div>
              <div class="benefit-item">
                {{ $t('ranking.freeCreditLabel') }}
                <span class="amount-teal">{{ selectedRank.freeCredit }}</span>
                {{ $t('ranking.baht') }}
              </div>
              <div class="benefit-item">
                {{ $t('ranking.successAmountLabel') }}
                <span class="amount-yellow">{{
                  selectedRank.successAmount
                }}</span>
                {{ $t('ranking.baht') }}
              </div>
              <div class="benefit-item">
                <template v-if="selectedRank.isLimitWithdraw">
                  {{ $t('ranking.maxWithdrawLabel') }}
                  <span class="amount-red">{{
                    selectedRank.limitWithdrawAmount
                  }}</span>
                  {{
                    selectedRank.limitWithdrawType === 'BAHT'
                      ? $t('ranking.baht')
                      : $t('ranking.times')
                  }}
                </template>
                <span v-else>{{ $t('ranking.noWithdrawLimit') }}</span>
              </div>
              <hr class="rank-divider" />
              <div
                class="rank-reset-note"
                v-html="
                  $t('ranking.resetNote', {
                    endDate: $options.filters.datetimeFormat(campaignEndDate)
                  })
                "
              ></div>
            </div>

            <div class="text-center m-t-15 m-b-10">
              <button
                class="button claim-btn is-primary"
                :disabled="!canClaimSelected"
                @click="claimBonus(selectedRank)"
              >
                {{ $t('ranking.claimBtn', { bonus: selectedRank.bonus }) }}
              </button>
            </div>
            <div class="status-box m-t-10">
              {{
                selectedRank.isClaimed
                  ? $t('ranking.alreadyClaimedMsg')
                  : canClaimSelected
                  ? $t('ranking.goalReachedMsg')
                  : $t('ranking.goalNotReachedMsg')
              }}
            </div>
          </section>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import apiRanking from '@/api/ranking'
import RankingTimeline from '@/components/RankingTimeline'

export default {
  name: 'Ranking',
  components: { RankingTimeline },
  props: {
    page: String
  },
  data() {
    return {
      loading: false,
      milestones: [],
      progress: null,
      campaignEndDate: null,
      activeRankIndex: 0,
      modalActive: false,
      selectedRank: null
    }
  },
  computed: {
    ranks() {
      return this.milestones.map(m => ({
        id: m._id,
        order: m.order,
        name: m.name || `Milestone ${m.order}`,
        shortName: (m.name || `M${m.order}`).split(' ')[0].substring(0, 8),
        image: m.image || null,
        goal: m.depositRequired,
        bonus: m.rewardAmount,
        freeCredit: m.rewardAmount,
        percentFail: m.percentFail || null,
        successAmount: (m.rewardAmount * m.percentSuccess) / 100 || null,
        // successAmount:
        //   (m.rewardAmount * m.percentSuccess) / 100 - m.rewardAmount || null,
        isLimitWithdraw: !!m.isLimitWithdraw,
        limitWithdrawAmount: m.limitWithdrawAmount,
        limitWithdrawType: m.limitWithdrawType,
        color: m.color || null,
        isClaimed: m.isClaimed,
        isReached: m.isReached,
        canClaim: m.canClaim
      }))
    },
    userDeposit() {
      return this.progress ? this.progress.totalDeposit : 0
    },
    currentRankIndex() {
      if (!this.ranks.length) return 0
      // Show the tier the user is actively working toward:
      // first tier that is not yet reached (deposit hasn't hit its goal)
      const nextGoal = this.ranks.findIndex(r => !r.isReached)
      if (nextGoal !== -1) return nextGoal
      // All tiers reached — show last one
      return this.ranks.length - 1
    },
    currentRank() {
      return this.ranks[this.currentRankIndex] || {}
    },
    activeRank() {
      return this.ranks[this.activeRankIndex] || this.currentRank
    },
    claimedCount() {
      return this.ranks.filter(r => r.isClaimed).length
    },
    canClaim() {
      return !!this.activeRank.canClaim
    },
    canClaimSelected() {
      return !!this.selectedRank && !!this.selectedRank.canClaim
    },
    statusMessage() {
      if (!this.milestones.length) return ''
      if (this.activeRank.isClaimed) return this.$t('ranking.alreadyClaimedMsg')
      if (this.canClaim) return this.$t('ranking.goalReachedMsg')
      return this.$t('ranking.goalNotReachedMsg')
    }
  },
  created() {
    this.$emit('childinit', this.page)
    this.fetchCurrent()
  },
  methods: {
    async fetchCurrent() {
      this.loading = true
      const overlay = this.$loading.show()
      try {
        const data = await apiRanking.getCurrent()
        if (data) {
          this.milestones = data.milestones || []
          this.progress = data.progress
          this.campaignEndDate = data.campaignEndDate || null
          this.$nextTick(() => {
            this.activeRankIndex = this.currentRankIndex
          })
        } else {
          this.milestones = []
          this.progress = null
          this.activeRankIndex = 0
        }
      } catch (err) {
        this.milestones = []
        this.progress = null
      } finally {
        overlay.hide()
        this.loading = false
      }
    },
    isCurrentRank(rank) {
      return rank.id === this.currentRank.id
    },
    openRankDetail(rank) {
      this.selectedRank = rank
      this.modalActive = true
      this.disabledBodyScroll(true)
    },
    closeModal() {
      this.modalActive = false
      this.selectedRank = null
      this.disabledBodyScroll(false)
    },
    claimBonus(rank) {
      const target =
        (rank && rank.canClaim !== undefined ? rank : null) || this.activeRank
      if (!target.canClaim) return
      const loading = this.$loading.show()
      setTimeout(async () => {
        try {
          await apiRanking.claim(target.order)
          if (this.modalActive) this.closeModal()
          this.$swal(
            this.$t('modals.successMsg'),
            this.$t('ranking.claimSuccessMsg', { bonus: target.bonus }),
            'success'
          )
          await this.fetchCurrent()
        } catch (err) {
          const reason =
            (err.response && err.response.data && err.response.data.message) ||
            (typeof err === 'string' ? err : null)
          const msg =
            reason && this.$te(`ranking.error.${reason}`)
              ? this.$t(`ranking.error.${reason}`)
              : reason || this.$t('error')
          this.$swal(this.$t('modals.errorMsg'), msg, 'error')
        } finally {
          loading.hide()
        }
      }, 200)
    },
    disabledBodyScroll(isOpen) {
      document.documentElement.style.overflow = isOpen ? 'hidden' : 'auto'
    }
  }
}
</script>

<style lang="scss" scoped>
.reward-claimed-label {
  text-align: center;
  margin-top: 10px;
  font-size: 0.85rem;
  opacity: 0.75;
  color: #ffffff;

  .reward-claimed-count {
    font-size: 1rem;
    font-weight: 600;
    margin-top: 2px;
    letter-spacing: 0.2em;

    .claimed-x {
      font-size: 1.2rem;
      font-weight: 800;
      color: $success;
    }
  }
}

.page-header {
  padding: 5px 0 15px;

  .page-title {
    font-size: 1.4rem;
    font-weight: 800;
    color: $text-color;
  }
}

.current-rank-section {
  padding: 5px 5px 20px;

  .rank-name-display {
    font-size: 1.8rem;
    font-weight: 900;
    letter-spacing: 2px;
    color: $text-color;
  }

  .rank-badge-area {
    .rank-badge-placeholder {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      background: rgba($text-color, 0.1);
      border: 2px solid rgba($text-color, 0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 2rem;
      font-weight: 700;
      color: rgba($text-color, 0.7);
    }

    img {
      width: 80px;
      height: 80px;
      object-fit: contain;
      filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.5));
    }
  }
}

.rank-card {
  background: rgba(20, 20, 40, 0.75);
  border-radius: 15px;
  padding: 15px 20px;
  border: 1px solid rgba($text-color, 0.08);
  backdrop-filter: blur(4px);

  .progress-header {
    color: #ffffff;
    font-size: 0.9rem;
    margin-bottom: 10px;

    .goal-amount {
      color: $warning;
      font-weight: 700;
      font-size: 1.2rem;
    }
  }

  .rank-progress {
    height: 10px;
    border-radius: 5px;
    margin-bottom: 5px;

    &::-webkit-progress-bar {
      background: rgba($text-color, 0.1);
      border-radius: 5px;
    }

    &::-webkit-progress-value {
      background: linear-gradient(to right, $primary, $light-primary);
      border-radius: 5px;
    }

    &::-moz-progress-bar {
      background: linear-gradient(to right, $primary, $light-primary);
      border-radius: 5px;
    }
  }
}

.claim-btn {
  width: 100%;

  &[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.status-box {
  // background: rgba($primary, 0.15);
  // border: 1px solid rgba($primary, 0.35);
  border-radius: 10px;
  padding: 12px 15px;
  color: $text-color;
  font-size: 0.9rem;
  text-align: center;
}

.benefits-card {
  text-align: center;

  .benefits-title {
    font-size: 1.3rem;
    font-weight: 700;
    color: $text-color;
    margin-bottom: 15px;
  }

  .benefit-item {
    font-size: 1rem;
    color: $text-color;
    margin: 8px 0;
  }

  .amount-teal {
    color: $info;
    font-weight: 700;
    font-size: 1.1rem;
  }

  .amount-red {
    color: $danger;
    font-weight: 700;
    font-size: 1.1rem;
  }

  .amount-yellow {
    color: $warning;
    font-weight: 700;
    font-size: 1.1rem;
  }

  .rank-divider {
    border: none;
    border-top: 1px solid rgba($text-color, 0.15);
    margin: 15px 0;
  }

  .rank-reset-note {
    color: $danger;
    font-size: 0.85rem;
  }
}

.modal-rank-name {
  font-size: 1.8rem !important;
  color: $box-text !important;
}

.modal-badge {
  .rank-badge-placeholder {
    width: 60px;
    height: 60px;
    font-size: 1.6rem;
  }

  img {
    width: 60px;
    height: 60px;
  }
}

.text-shadow {
  text-shadow: -1px -1px 0px #000, 0px -1px 0px #000, 1px -1px 0px #000,
    -1px 0px 0px #000, 1px 0px 0px #000, -1px 1px 0px #000, 0px 1px 0px #000,
    1px 1px 0px #000;
}

::v-deep .modal-card-body {
  .rank-card {
    background: transparent;
    border: 1px solid rgba($box-text, 0.25);
    backdrop-filter: none;

    .progress-header,
    .benefit-item,
    .benefits-title {
      color: $box-text;
    }

    .rank-progress {
      &::-webkit-progress-bar {
        background: rgba($box-text, 0.15);
      }
    }
  }

  .benefits-card {
    background: transparent;
    border: 1px solid rgba($box-text, 0.25);
    backdrop-filter: none;
  }

  .status-box {
    color: $box-text;
  }
}
</style>
